import React, { FC, useCallback, useMemo, useState } from "react";
import { AiOutlineClockCircle, AiOutlineInfoCircle } from "react-icons/ai";
import { RiBuilding3Line } from "react-icons/ri";
import settings from "@settings";
import {
  trainingElement,
  trainingElementuttonContainer,
  trainingImage,
} from "../styles";
import { FlexBox, FullButton, Label } from "@ui";
import { useFindImages, useGetGroup, useGetLocation } from "@provider";
import { TrainingCalendarElementProps } from "../types";
import { PatstoreGroup, PatstoreLocation } from "@types";
import { SportSlideIn } from "@components";
import { GatsbyImage } from "gatsby-plugin-image";

const TrainingCalendarElement: FC<TrainingCalendarElementProps> = ({
  training,
}) => {
  const { groups } = useGetGroup();
  const { locations } = useGetLocation();
  const [modalState, setModalState] = useState(false);
  const { findImage } = useFindImages();

  const group: PatstoreGroup | undefined = useMemo(() => {
    return groups.find((group) => group.objectId === training.group_id);
  }, [training.group_id, groups]);

  const groupLocation: PatstoreLocation | null | undefined = useMemo(() => {
    if (!locations) {
      return undefined;
    }
    const location = locations.find(
      (location) => location.objectId === training.place.location,
    );
    return location;
  }, [training.place.location, locations]);

  const getImageHandler = useCallback(() => {
    if (!group) {
      return (
        <div
          css={{
            width: "100%",
            height: "150px",
            backgroundColor: settings.colors.grey,
          }}
        />
      );
    }

    if (
      group.team?.image &&
      typeof group.team.image === "string" &&
      findImage(group.team.image)
    ) {
      return (
        <GatsbyImage
          alt={group.team?.name}
          image={findImage(group.team.image)}
        />
      );
    } else {
      return (
        <GatsbyImage alt={group.team?.name} image={findImage(group.image)} />
      );
    }
  }, []);

  if (!group) {
    return null;
  }

  return (
    <>
      <div css={trainingElement} id={training.id} key={training.id}>
        <FlexBox
          direction="column"
          align="stretch"
          justify="flex-start"
          gap={settings.sizes.medium}
        >
          <div css={trainingImage}>{getImageHandler()}</div>
          <FlexBox
            direction="column"
            align="flex-start"
            justify="center"
            gap={settings.sizes.small}
            styles={{ padding: settings.padding.small }}
          >
            <FlexBox
              direction="row"
              align="center"
              justify="flex-start"
              gap={settings.sizes.small}
            >
              {training.categories.length > 0
                ? training.categories.map((category) => {
                    return <Label text={category} key={category} />;
                  })
                : null}
            </FlexBox>
            <h4>{group.title}</h4>
            <div>
              <p css={{ fontWeight: 600, maxWidth: "240px" }}>
                <span css={{ marginRight: ".3rem", minWidth: "18px" }}>
                  <AiOutlineClockCircle />
                </span>
                {training.start && training.end
                  ? `${training.start} - ${training.end}`
                  : "-"}
              </p>
            </div>
            <div>
              <p css={{ fontWeight: 600, maxWidth: "240px" }}>
                <span css={{ marginRight: ".3rem", minWidth: "18px" }}>
                  <RiBuilding3Line />
                </span>{" "}
                {training.location}
              </p>
            </div>
            {group.description && (
              <div>
                <p>
                  <span
                    css={{
                      color: settings.colors.primary,
                      marginRight: ".3rem",
                    }}
                  >
                    <AiOutlineInfoCircle />
                  </span>{" "}
                  {group.description}
                </p>
              </div>
            )}
          </FlexBox>
        </FlexBox>
        <div css={trainingElementuttonContainer}>
          <FullButton
            text="mehr Infos"
            color="primary"
            onClick={() => setModalState(!modalState)}
          />
        </div>
      </div>
      <SportSlideIn
        group={group}
        setModalState={setModalState}
        modalState={modalState}
        location={groupLocation?.title || ""}
      />
    </>
  );
};

export default React.memo(TrainingCalendarElement);
